import React from 'react'
import { VB_W, VB_H } from './geo'
import type { WorldPoint } from '@/analysis/apiTypes'
import type { Tier } from '@/analysis/wsTypes'
import { fmtDemuxRate, fmtLagMs } from '@/components/table/format'
import { getSevColor } from '@/constants'

interface Props {
  hover: { p: WorldPoint; x: number; y: number } | null
  dimension?: string
}

// Hover tooltip drawn in SVG (so it scales/positions with the markers layer).
const SEV_TEXT: Record<number, string> = { 0: 'CRITICAL', 1: 'warning', 2: 'healthy' }

const METRIC_LABEL: Record<string, string> = {
  demux_in_rate:       'Demux rate',
  demux_to_decode_lag: 'Demux→Dec lag',
  render_ratio:        'Render in/out',
  download_kbps:       'Download',
  jitter_ms:           'Jitter',
  packet_delay_ms:     'Packet delay',
  drop_frame_ms:       'Dropped frames',
  p_frame_delay_ms:    'P-frame delay',
  i_frame_delay_ms:    'I-frame delay',
}

export function Tooltip({ hover, dimension }: Props) {
  if (!hover) return null
  const { p, x, y } = hover

  const lines: string[] = []
  const label = p.label ?? p.id
  for (const part of String(label).split('\n')) {
    const t = part.trim()
    if (t) lines.push(t)
  }

  const clusterStudios = (p as any).clusterStudios as string[] | undefined
  const clusterIndex = (p as any).clusterIndex as number | undefined
  if (clusterStudios && clusterStudios.length > 1) {
    const i = typeof clusterIndex === 'number' ? clusterIndex : 0
    lines.push(`Studio ${i + 1} of ${clusterStudios.length}: ${clusterStudios[i]}`)
  }

  const isCluster = String(p.id).startsWith('cluster:')
  const isLink = String(p.id).startsWith('link:')

  if (isLink) lines.push('CDN / network delivery path')

  const avgSev = (p as any).avgSeverity
  const worstMetric = (p as any).worstMetric
  if (!isCluster && (avgSev === 0 || avgSev === 1 || avgSev === 2)) {
    const driver = (avgSev !== 2 && typeof worstMetric === 'string' && worstMetric)
      ? ` — worst: ${METRIC_LABEL[worstMetric] ?? worstMetric}`
      : ''
    lines.push(`status: ${SEV_TEXT[avgSev]}${driver}`)
  }

  if (!isCluster && dimension !== 'stream') lines.push(`streams: ${p.total}`)

  if (!isCluster && typeof (p as any).usersTotal === 'number') lines.push(`users: ${(p as any).usersTotal}`)

  if (!isCluster && typeof (p as any).distinctIps === 'number' && (p as any).distinctIps > 0) {
    lines.push(`IPs: ${(p as any).distinctIps}`)
  }

  const m = (p as any).metrics as Record<string, { displayValue?: number | null; tier?: Tier | null }> | undefined
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const demuxInRate = num(m?.demuxInRate?.displayValue) ?? num((p as any).demuxInRate)
  const demuxInRateTier = m?.demuxInRate?.tier ?? (p as any).demuxInRateTier ?? null
  const demuxToDecodeLagMs = num(m?.demuxToDecodeLag?.displayValue) ?? num((p as any).demuxToDecodeLagMs)
  const renderRatio = num(m?.renderRatio?.displayValue) ?? num((p as any).renderRatio) ?? num((p as any).renderInOutGap)
  const downloadKbps = num(m?.downloadKbps?.displayValue) ?? num((p as any).downloadKbps)

  if (!isCluster) {
    if (demuxInRate != null) lines.push(`Demux rate: ${fmtDemuxRate(demuxInRate, demuxInRateTier)}`)
    if (demuxToDecodeLagMs != null) lines.push(`Demux→Dec: ${fmtLagMs(demuxToDecodeLagMs)}`)
    if (renderRatio != null) lines.push(`Render in/out: ${renderRatio.toFixed(2)}`)
    if (downloadKbps != null) lines.push(`Download: ${Math.round(downloadKbps)}kbps`)
  }

  const sevForAccent =
    (avgSev === 0 || avgSev === 1 || avgSev === 2)
      ? (avgSev as 0 | 1 | 2)
      : (typeof p.sev === 'number' && (p.sev === 0 || p.sev === 1 || p.sev === 2) ? p.sev : null)
  const accent = sevForAccent == null ? 'var(--bd-2, rgba(255,255,255,0.25))' : getSevColor(sevForAccent)

  const w = 260
  const pad = 12
  const textW = w - pad * 2 - 10
  // Estimate height from wrapped lines (~18px per line, header heavier).
  const estLines = lines.reduce((n, t, i) => {
    const charsPer = i === 0 ? 28 : 34
    return n + Math.max(1, Math.ceil(t.length / charsPer))
  }, 0)
  const h = Math.min(220, 16 + estLines * 18 + 12)
  const bx = Math.min(Math.max(x + 14, 8), VB_W - w - 8)
  const by = Math.min(Math.max(y - h - 14, 8), VB_H - h - 8)

  return (
    <g pointerEvents="none">
      <rect
        x={bx + 2} y={by + 3} width={w} height={h} rx={12}
        fill="rgba(0,0,0,0.38)"
      />
      <rect
        x={bx} y={by} width={w} height={h} rx={10}
        fill="var(--bg-2)"
        fillOpacity="0.97"
        stroke="var(--bd-1)"
        strokeWidth="1"
      />
      <rect x={bx + 7} y={by + 12} width={3} height={h - 24} rx={1.5} fill={accent} opacity={0.95} />
      <foreignObject x={bx + pad + 6} y={by + 8} width={textW} height={h - 16}>
        <div
          style={{
            fontFamily: 'inherit',
            color: 'var(--t-1)',
            fontSize: 12,
            lineHeight: '18px',
            overflow: 'hidden',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            paddingRight: 4,
          }}
        >
          {lines.map((t, i) => (
            <div
              key={`${p.id}-${i}`}
              style={{
                color: i === 0 ? 'var(--t-0)' : 'var(--t-1)',
                fontWeight: i === 0 ? 700 : 500,
                fontSize: i === 0 ? 13 : 12,
                marginBottom: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
              }}
            >
              {i === 0 && (
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: accent,
                    marginTop: 5,
                    flexShrink: 0,
                    display: 'inline-block',
                  }}
                />
              )}
              <span>{t}</span>
            </div>
          ))}
        </div>
      </foreignObject>
    </g>
  )
}
